import { v4 as uuid } from 'uuid';
import { map, mergeMap, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatServices from '@medsurf/flat-services';
import * as FlatFacades from '../../internal';

@Injectable()
export class NodeSharedFacade {
  constructor(protected store: Store,
              protected apiControlService: FlatServices.ApiControlService) {
  }

  //<editor-fold desc="Selectors">
  
  entities$ = this.store.select(FlatStates.NodeEntityState.entities$);
  elearnings$ = this.store.select(FlatStates.NodeEntityState.elearnings$);
  isTrainingResult$ = this.store.select(FlatStates.NodeEntityState.isTrainingResult$);
  typedRouteElements$ = this.store.select(FlatStates.NodeEntityState.typedRouteElements$);
  currentSelectedRouteElement$ = this.store.select(FlatStates.NodeEntityState.currentSelectedRouteElement$);
  currentSelectedParentRouteElement$ = this.store.select(FlatStates.NodeEntityState.currentSelectedParentRouteElement$);
  currentSelectedElearningNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedElearningNode$);
  currentSelectedSubjectNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedSubjectNode$);
  currentSelectedChapterNodes$ = this.store.select(FlatStates.NodeEntityState.currentSelectedChapterNodes$);
  currentSelectedChapterNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedChapterNode$);
  currentSelectedBlockNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedBlockNode$);
  currentSelectedNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedNode$);

  currentSelectedNodeSettings$ = this.store.select(FlatStates.NodeEntityState.currentSelectedNodeSettings$);
  selectedNodeAndParentsSettings$ = this.store.select(FlatStates.NodeEntityState.selectedNodeAndParentsSettings$);
  currentSelectedSettings$ = this.store.select(FlatStates.NodeEntityState.currentSelectedSettings$);

  currentSelectedCaseStartUrl$ = this.store.select(FlatStates.NodeEntityState.currentSelectedCaseStartUrl$);
  currentTypedExpanded$ = this.store.select(FlatStates.NodeEntityState.currentTypedExpanded$);

  isSubject$ = this.store.select(FlatStates.NodeEntityState.isSubject$);
  isChapter$ = this.store.select(FlatStates.NodeEntityState.isChapter$);
  isSlide$ = this.store.select(FlatStates.NodeEntityState.isSlide$);
  
  typedPages$ = this.store.select(FlatStates.PageEntityState.typedEntities$);

  // Page Entity State
  currentSelectedChapterPage$ = this.store.select(FlatStates.PageEntityState.currentSelectedChapterPage$);
  currentSelectedBlockPage$ = this.store.select(FlatStates.PageEntityState.currentSelectedBlockPage$);
  currentSelectedSlidePage$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlidePage$);
  currentSelectedPage$ = this.store.select(FlatStates.PageEntityState.currentSelectedPage$);
  currentSelectedChapterPageType$ = this.store.select(FlatStates.PageEntityState.currentSelectedChapterPageType$);
  currentSelectedSlideNodesFiltered$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlideNodesFiltered$);
  isBlock$ = this.store.select(FlatStates.PageEntityState.isBlock$);
  isQuizStart$ = this.store.select(FlatStates.PageEntityState.isQuizStart$);

  // Media Entity State
  currentSelectedPageViewerPdf$ = this.store.select(FlatStates.MediaEntityState.currentSelectedPageViewerPdf$);
  currentSelectedPageAuthorPdf$ = this.store.select(FlatStates.MediaEntityState.currentSelectedPageAuthorPdf$);

  // Snapshots
  public snapshot_currentSelectedRouteElement = () => this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedRouteElement$);
  public snapshot_currentSelectedParentRouteElement = () => this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedParentRouteElement$);
  public snapshot_currentSelectedBlockNode = () => this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedBlockNode$);
  public snapshot_currentSelectedSiblingNodes = () => this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedSiblingNodes$);
  public snapshot_blockUiType = () => this.store.selectSnapshot(FlatStates.HistoryControlState.blockUiType$);
  public snapshot_currentSelectedPage = () => this.store.selectSnapshot(FlatStates.PageEntityState.currentSelectedPage$);

  //</editor-fold>

  //<editor-fold desc="Snapshot Selectors">

  public snapshot_elearnings = () => this.store.selectSnapshot(FlatStates.NodeEntityState.elearnings$);
  
  //</editor-fold>

  //<editor-fold desc="Lazy Selectors">

  isNodeCurrent$ = (id: string) => this.store.select(FlatStates.NodeEntityState.isNodeCurrent$).pipe(map(filterFn => filterFn(id)));
  isNodeSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isNodeSelected$).pipe(map(filterFn => filterFn(id)));
  
  nodeById$ = (id: string) => this.store.select(FlatStates.NodeEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  nodeByIds$ = (ids: string[]) => this.store.select(FlatStates.NodeEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));


  // Medias
  hasBulkEditMedia$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditMedia$).pipe(map(filterFn => filterFn(id)));

  // Nodes
  hasBulkEditNode$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditNode$).pipe(map(filterFn => filterFn(id)));
  hasAllBulkEditNodes$ = (ids: string[]) => this.store.select(FlatStates.BulkEditAuthorState.hasAllBulkEditNodes$).pipe(map(filterFn => filterFn(ids)));
  
  // Pages
  hasBulkEditPage$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditPage$).pipe(map(filterFn => filterFn(id)));
  hasAllBulkEditPages$ = (ids: string[]) => this.store.select(FlatStates.BulkEditAuthorState.hasAllBulkEditPages$).pipe(map(filterFn => filterFn(ids)));

  //</editor-fold>

  //<editor-fold desc="Helpers">

  getSlideStatusIcon(slide: FlatModels.PageEntityModels.Slide) {
    if (slide?.status) {
      switch (slide.status) {
        case FlatModels.PageEntityModels.SlideStatus.enum.draft:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.STATUS_DRAFT;
        case FlatModels.PageEntityModels.SlideStatus.enum.in_review:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.STATUS_IN_REVIEW;
        case FlatModels.PageEntityModels.SlideStatus.enum.review_not_ok:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.STATUS_REVIEW_NOT_OK;
        case FlatModels.PageEntityModels.SlideStatus.enum.final:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.STATUS_FINAL;
        default:
          return FlatModels.ButtonDesignModels.ButtonDefaultIcons.STATUS_DEFAULT;
      }
    }
    return '';
  }

  //</editor-fold>

  //<editor-fold desc="Requests">

  requestNodeById(id: string) {
    return this.store.dispatch(new FlatActions.NodeControlActions.GetNodeById(id));
  }

  requestNodesByParentId(parentId: string | null) {
    return this.store.dispatch(new FlatActions.NodeControlActions.GetNodesByParentId(parentId));
  }
	
  requestGetSlideDataByPageId(id: string) {
    return this.store.dispatch(new FlatActions.SlideControlActions.GetSlideDataByPageId(id));
  }

  requestGetSlideDataBySlideId(slideId: number) {
    return this.store.dispatch(new FlatActions.SlideControlActions.GetSlideDataBySlideId(slideId));
  }

  requestNodesByCurrentRoute() {
    return this.store.dispatch(new FlatActions.NodeControlActions.GetNodesByCurrentRoute());
  }

  requestElearnings() {
    return this.requestNodesByParentId(null);
  }

  requestRouteById(id: string) {
    return this.store.dispatch(new FlatActions.NodeControlActions.GetRouteById(id));
  }

  requestAddElearning(name: string,
                      title: string) {
    // Get Data
    const typedElearnings = this.store.selectSnapshot(FlatStates.NodeEntityState.typedElearnings$);
    
    // Create New Page
    const newPage: FlatModels.PageEntityModels.Folder = {
      id: uuid(),
      title: title,
      description: '',
      body: '',
      type: FlatModels.PageEntityModels.PageType.enum.Folder,
      themeColor: null,
    }
    
    // Get Elearnings
    const elearnings = Object.keys(typedElearnings);
    
    // Create New Node
    const {route} = FlatFacades.NodeEntityFacade.getPageCopyCountAndRoute(title, null, null, this.store);
    const newNode: FlatModels.NodeEntityModels.NodeEntityType = {
      id: uuid(),
      shopId: null,
      parent: null,
      parentId: null,
      position: elearnings.length || 0,
      page: newPage.id,
      children: [],
      route: route,
      hidden: false,
      restricted: false,
      shares: [],
      settings: {},
    };

    // Save Page And Node
    return this.store.dispatch(new FlatActions.PageEntityActions.AddEntity(newPage)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.SlideControlActions.AddAvailableSlide(newPage.id))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.NodeEntityActions.AddEntity(newNode))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
      mergeMap((data) => {
        const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
        this.store.dispatch(new Navigate([typedRouteElements[newNode.id].path]));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView(newNode.id));
        return of(data);
      }),
    );
  }
  
  requestAddNode(routeElement: FlatModels.NodeEntityModels.RouteElement,
                 name: string,
                 title: string,
                 isTraining = false) {
    // Get Data
    const typedNodes = this.store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    const typedElearnings = this.store.selectSnapshot(FlatStates.NodeEntityState.typedElearnings$);
    
    // Get Parent Node
    const parentNode = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.nodeById$(routeElement.id)));
    
    // Get Elearnings
    const elearnings = Object.keys(typedElearnings);

    // Create New Page
    let newPage: FlatModels.PageEntityModels.Folder
      | FlatModels.PageEntityModels.Grid
      | FlatModels.PageEntityModels.Training
      | FlatModels.PageEntityModels.Slide;
    switch (routeElement.depth) {
      case 0:
        newPage = {
          id: uuid(),
          title: title,
          description: '',
          body: '',
          type: FlatModels.PageEntityModels.PageType.enum.Folder,
          themeColor: FlatModels.ColorConfigModels.themeColorList[0].value
        } as FlatModels.PageEntityModels.Folder;
        break;
      case 1:
        newPage = {
          id: uuid(),
          title: title,
          description: '',
          body: '',
          type: FlatModels.PageEntityModels.PageType.enum.Folder,
        } as FlatModels.PageEntityModels.Folder;
        break;
      case 2:
        newPage = {
          id: uuid(),
          title: title,
          type: FlatModels.PageEntityModels.PageType.enum.Grid,
        } as FlatModels.PageEntityModels.Grid;
        break;
      case 3:
        if (isTraining) {
          newPage = {
            id: uuid(),
            title: title,
            type: FlatModels.PageEntityModels.PageType.enum.Training,
            header: '',
            description: '',
            body: '',
            slideId: null,
            mediaType: null,
            sideBarSize: 3,
            modality: null,
            status: FlatModels.PageEntityModels.SlideStatus.enum.empty,
            layers: [],
            groups: [],
            questions: [],
            pois: [],
            annotations: [],
            defaults: {
              labelColor: null,
              pointerColor: null,
              labelFontSize: null,
            },
            restricted: false,
            hidden: false,
          } as FlatModels.PageEntityModels.Training;
        } else {
          newPage = {
            id: uuid(),
            title: title,
            type: FlatModels.PageEntityModels.PageType.enum.Slide,
            header: '',
            description: '',
            body: '',
            slideId: null,
            mediaType: null,
            sideBarSize: 3,
            modality: null,
            status: FlatModels.PageEntityModels.SlideStatus.enum.empty,
            layers: [],
            groups: [],
            questions: [],
            pois: [],
            annotations: [],
            defaults: {
              labelColor: null,
              pointerColor: null,
              labelFontSize: null,
            },
            restricted: false,
            hidden: false,
          } as FlatModels.PageEntityModels.Slide;
        }
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.NodeSharedFacade.requestAddNode"
        );
    }

    // Create New Node
    const {route} = FlatFacades.NodeEntityFacade.getPageCopyCountAndRoute(title, parentNode?.id ?? null, null, this.store);
    const newNode: FlatModels.NodeEntityModels.NodeEntityType = {
      id: uuid(),
      shopId: null,
      parent: routeElement.id,
      parentId: routeElement.id,
      position: parentNode?.children.length || elearnings.length || 0,
      page: newPage.id,
      children: [],
      route: route,
      hidden: false,
      restricted: false,
      shares: [],
      settings: {},
    };

    // Update Parent Node
    if (parentNode) {
      parentNode.children.push(newNode.id);

      // Save Page And Node
      return this.store.dispatch(new FlatActions.PageEntityActions.AddEntity(newPage)).pipe(
        mergeMap((data) => {
          if (newPage.type === FlatModels.PageEntityModels.PageType.enum.Slide || newPage.type === FlatModels.PageEntityModels.PageType.enum.Training) {
            return FlatFacades.LayerEntityFacade.addLayer(this.store, structuredClone(newPage) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training);
          }
          return of(data);
        }),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.SlideControlActions.AddAvailableSlide(newPage.id))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.NodeEntityActions.AddEntity(newNode))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(parentNode))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
            name,
            this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
          ))
        ),
        mergeMap((result) => {
          const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
          this.store.dispatch(new Navigate([typedRouteElements[newNode.id].path]));
          if (routeElement.depth < 3) {
            this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView(newNode.id));
          }
          return of(result);
        }),
      );
    } else {
      // Save Page And Node
      return this.store.dispatch(new FlatActions.PageEntityActions.AddEntity(newPage)).pipe(
        mergeMap((data) => {
          if (newPage.type === FlatModels.PageEntityModels.PageType.enum.Slide || newPage.type === FlatModels.PageEntityModels.PageType.enum.Training) {
            return FlatFacades.LayerEntityFacade.addLayer(this.store, structuredClone(newPage) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training);
          }
          return of(data);
        }),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.SlideControlActions.AddAvailableSlide(newPage.id))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.NodeEntityActions.AddEntity(newNode))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
            name,
            this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
          ))
        ),
        mergeMap((result) => {
          const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
          this.store.dispatch(new Navigate([typedRouteElements[newNode.id].path]));
          if (routeElement.depth < 3) {
            this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView(newNode.id));
          }
          return of(result);
        }),
      );
    }
  }

  requestAddNodeAfterCurrent(routeElement: FlatModels.NodeEntityModels.RouteElement,
                             currentRouteElement: FlatModels.NodeEntityModels.RouteElement | null,
                             name: string,
                             title: string,
                             isTraining = false) {
    // Get Data
    const typedNodes = this.store.selectSnapshot(FlatStates.NodeEntityState.typedEntities$);
    const typedElearnings = this.store.selectSnapshot(FlatStates.NodeEntityState.typedElearnings$);
    
    // Get Parent Node
    const parentNode = structuredClone(typedNodes[routeElement.id]);
    
    // Get Elearnings
    const elearnings = Object.keys(typedElearnings);

    // Create New Page
    // let newPage: FlatModels.PageEntityModels.PageEntityType;
    let newPage: FlatModels.PageEntityModels.Folder
      | FlatModels.PageEntityModels.Grid
      | FlatModels.PageEntityModels.Training
      | FlatModels.PageEntityModels.Slide;
    switch (routeElement.depth) {
      case 0:
        newPage = {
          id: uuid(),
          title: title,
          description: '',
          body: '',
          type: FlatModels.PageEntityModels.PageType.enum.Folder,
          themeColor: FlatModels.ColorConfigModels.themeColorList[0].value
        } as FlatModels.PageEntityModels.Folder;
        break;
      case 1:
        newPage = {
          id: uuid(),
          title: title,
          description: '',
          body: '',
          type: FlatModels.PageEntityModels.PageType.enum.Folder,
        } as FlatModels.PageEntityModels.Folder;
        break;
      case 2:
        newPage = {
          id: uuid(),
          title: title,
          description: '',
          body: '',
          type: FlatModels.PageEntityModels.PageType.enum.Grid,
        } as FlatModels.PageEntityModels.Grid;
        break;
      case 3:
        if (isTraining) {
          newPage = {
            id: uuid(),
            title: title,
            type: FlatModels.PageEntityModels.PageType.enum.Training,
            header: '',
            description: '',
            body: '',
            slideId: null,
            mediaType: null,
            sideBarSize: 3,
            modality: null,
            status: FlatModels.PageEntityModels.SlideStatus.enum.empty,
            layers: [],
            groups: [],
            questions: [],
            pois: [],
            annotations: [],
            defaults: {
              labelColor: null,
              pointerColor: null,
              labelFontSize: null,
            },
            restricted: false,
            hidden: false,
          } as FlatModels.PageEntityModels.Training;
        } else {
          newPage = {
            id: uuid(),
            title: title,
            type: FlatModels.PageEntityModels.PageType.enum.Slide,
            header: '',
            body: '',
            description: '',
            slideId: null,
            mediaType: null,
            sideBarSize: 3,
            modality: null,
            status: FlatModels.PageEntityModels.SlideStatus.enum.empty,
            layers: [],
            groups: [],
            questions: [],
            pois: [],
            annotations: [],
            defaults: {
              labelColor: null,
              pointerColor: null,
              labelFontSize: null,
            },
            restricted: false,
            hidden: false,
          } as FlatModels.PageEntityModels.Slide;
        }
        break;
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.NodeSharedFacade.requestAddNodeAfterCurrent"
        );
    }

    // Create New Node
    const {route} = FlatFacades.NodeEntityFacade.getPageCopyCountAndRoute(title, parentNode?.id ?? null, null, this.store);
    const newNode: FlatModels.NodeEntityModels.NodeEntityType = {
      id: uuid(),
      shopId: null,
      parent: routeElement.id,
      parentId: routeElement.id,
      position: 0,
      page: newPage.id,
      children: [],
      route: route,
      hidden: false,
      restricted: false,
      shares: [],
      settings: {},
    };
    
    // Check Position here
    const arrangedChildren: FlatModels.NodeEntityModels.NodeEntityType[] = [];
    const position = parentNode?.children.length || 0;
    if (currentRouteElement) {
      // Get Children
      const children = parentNode.children.map(childId => typedNodes[childId]);
      
      // Sort children
      for (let i = 0; i < children.length; i++) {
        const child = children.find(c => c.position === i);
        if (child) {
          arrangedChildren.push(child);
          if (child.id === currentRouteElement.id) {
            arrangedChildren.push(newNode);
          }
        }
      }
    }
    
    // Update New Node
    newNode.position = position;

    // Update Parent Node
    if (parentNode) {
      parentNode.children.push(newNode.id);

      // Save Page And Node
      return this.store.dispatch(new FlatActions.PageEntityActions.AddEntity(newPage)).pipe(
        mergeMap((data) => {
          if (newPage.type === FlatModels.PageEntityModels.PageType.enum.Slide || newPage.type === FlatModels.PageEntityModels.PageType.enum.Training) {
            return FlatFacades.LayerEntityFacade.addLayer(this.store, structuredClone(newPage) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training);
          }
          return of(data);
        }),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.SlideControlActions.AddAvailableSlide(newPage.id))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.NodeEntityActions.AddEntity(newNode))
        ),
        mergeMap((data) => {
          if (currentRouteElement) {
            return this.store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(parentNode, arrangedChildren));
          }
          return of(data);
        }),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(parentNode))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
            name,
            this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
          ))
        ),
        mergeMap((result) => {
          const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
          this.store.dispatch(new Navigate([typedRouteElements[newNode.id].path]));
          if (routeElement.depth < 3) {
            this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView(newNode.id));
          }
          return of(result);
        }),
      );
    } else {
      // Save Page And Node
      return this.store.dispatch(new FlatActions.PageEntityActions.AddEntity(newPage)).pipe(
        mergeMap((data) => {
          if (newPage.type === FlatModels.PageEntityModels.PageType.enum.Slide || newPage.type === FlatModels.PageEntityModels.PageType.enum.Training) {
            return FlatFacades.LayerEntityFacade.addLayer(this.store, structuredClone(newPage) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training);
          }
          return of(data);
        }),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.SlideControlActions.AddAvailableSlide(newPage.id))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.NodeEntityActions.AddEntity(newNode))
        ),
        mergeMap((data) => {
          if (currentRouteElement) {
            return this.store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(null, arrangedChildren));
          }
          return of(data);
        }),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
            name,
            this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
          ))
        ),
        mergeMap((result) => {
          const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
          this.store.dispatch(new Navigate([typedRouteElements[newNode.id].path]));
          if (routeElement.depth < 3) {
            this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView(newNode.id));
          }
          return of(result);
        }),
      );
    }
  }

  requestUpdateNode(node: FlatModels.NodeEntityModels.NodeEntityType,
                    name: string) {
    // Update Node
    return this.store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(node)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveNode(node: FlatModels.NodeEntityModels.NodeEntityType,
                    name: string) {
    // Get Data
    const typedRouteElements = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$));
    const elearnings = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.elearnings$));
    
    // Get Parent Route Element
    let url = this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
    if (url === typedRouteElements[node.id].path && node.parentId) { // Only when url matches
      const parentRouteElement = typedRouteElements[node.parentId];
      url = parentRouteElement.path;
    }
    
    // Remove Node
    return FlatFacades.NodeEntityFacade.removeNodes(this.store, [node.id], elearnings).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          url
        ))
      )
    );
  }

  requestRearrangeNodes(node: FlatModels.NodeEntityModels.NodeEntityType | null,
                        children: FlatModels.NodeEntityModels.NodeEntityType[]) {
    // Rearrange Nodes
    return this.store.dispatch(new FlatActions.NodeEntityActions.RearrangeNodes(node, children)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.node_rearrange_nodes',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdatePage(page: FlatModels.PageEntityModels.PageEntityType,
                    name: string) {
    // Update Page
    return this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateNodeSettings(nodeId: string,
                            settings: FlatModels.NodeEntityModels.NodeSettings | null) {
    // Get From Url
    const fromUrl = this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$);

    // Get Node
    const node = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.nodeById$(nodeId)));
    if (node) {
      // Update Node
      node.settings = settings;

      // Update Node And Page + Navigate to New Route
      return this.store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(node)).pipe(
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
            'history.node_update_settings',
            this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$),
            fromUrl
          ))
        )
      );
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.node_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatFacades.NodeSharedFacade.requestUpdateRouteElement"
      );
    }
  }

  requestUpdateNodeRoute(nodeId: string,
                         newRoute: string) {
    // Get Typed Route Elements
    const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$)
    
    // Get From Url
    const fromUrl = this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$);

    // Get Node
    const node = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.nodeById$(nodeId)));
    if (node) {
      // Get Parent Node
      const parentNode = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.nodeById$(node.parentId)));
      
      // Update Node Route
      const {route} = FlatFacades.NodeEntityFacade.getPageCopyCountAndRoute(newRoute, parentNode?.id ?? null, node.id, this.store);
      node.route = route;
      
      // Get Current Route
      let parentRoute = '';
      if (node.parentId) {
        parentRoute = typedRouteElements[node.parentId]?.path + '/';
      }
      const currentRoute = parentRoute + node.route;

      // Update Node And Page + Navigate to New Route
      return this.store.dispatch(new FlatActions.NodeEntityActions.UpdateEntity(node)).pipe(
        mergeMap(() =>
          this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
            'history.node_update_route',
            currentRoute,
            fromUrl
          ))
        ),
        mergeMap(() =>
          this.store.dispatch(new Navigate([currentRoute]))
        ),
        mergeMap((result) => {
          // Ensure metadata display after update route
          setTimeout(() => {
            this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata))
          }, 100);
          return of(result);
        })
      );
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.node_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatFacades.NodeSharedFacade.requestUpdateNodeRoute"
      );
    }
  }

  requestGetNodeRouteFromTitle(nodeId: string,
                                  title: string) {
    // Get Node
    const node = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.nodeById$(nodeId)));

    if (node) {
      // Get Parent Node
      const parentNode = structuredClone(this.store.selectSnapshot(FlatStates.NodeEntityState.nodeById$(node.parentId)));
      
      // Update Node Route
      const {route} = FlatFacades.NodeEntityFacade.getPageCopyCountAndRoute(title, parentNode?.id ?? null, node.id, this.store);

      return route;
    }
    return '';
  }

  requestGenerateChapterPdf(id: string,
                            forceGeneration: boolean,
                            forAuthor: boolean) {
    return this.store.dispatch(new FlatActions.MediaAuthorActions.GenerateChapterPdf(
      id,
      forceGeneration,
      forAuthor
    ));
  }

  requestGenerateSlidePdf(id: string,
                          forAuthor: boolean) {
    return this.store.dispatch(new FlatActions.MediaAuthorActions.GenerateSlidePdf(
      id,
      forAuthor
    ));
  }

  //</editor-fold>
}
